'use client';

import React from 'react';
import Link from 'next/link';
import { m } from 'framer-motion';
import { Check, Crown, Tag, ArrowRight, Lock, Brain } from 'lucide-react';

interface Feature {
    text: string;
    locked?: boolean;
}

interface PricingCardProps {
    tier: string;
    price: string;
    period: string;
    yearlyPrice?: string;
    description: string;
    features: Feature[];
    highlight?: boolean;
    ctaText?: string;
    badge?: string;
    billingCycle: 'monthly' | 'yearly';
}

const PricingCard = ({
    tier, price, period, yearlyPrice, description, features,
    highlight = false, ctaText = 'Start Free', badge, billingCycle
}: PricingCardProps) => {
    const displayPrice = billingCycle === 'yearly' && yearlyPrice ? yearlyPrice : price;

    return (
        <m.div
            className={`relative p-8 rounded-[2rem] border transition-all duration-300 flex flex-col h-full ${highlight
                ? 'border-neutral-900 bg-neutral-900 shadow-2xl shadow-neutral-900/20'
                : 'border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-lg'
                }`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -6 }}
        >
            {badge && (
                <div className={`absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1.5 ${highlight ? 'bg-orange-500 text-white shadow-orange-500/30' : 'bg-neutral-900 text-white'}`}>
                    {highlight && <Crown size={11} className="text-white" />}
                    {badge}
                </div>
            )}

            <div className="mb-8">
                <h3 className={`text-xl font-black mb-4 ${highlight ? 'text-white' : 'text-neutral-900'}`}>{tier}</h3>
                <div className="flex items-baseline gap-1.5 mb-5">
                    <span className={`text-6xl font-black tracking-tight ${highlight ? 'text-white' : 'text-neutral-900'}`}>{displayPrice}</span>
                    <span className={`text-sm font-bold uppercase tracking-widest ${highlight ? 'text-orange-400' : 'text-neutral-400'}`}>{period}</span>
                </div>
                <p className={`text-sm leading-relaxed font-medium ${highlight ? 'text-neutral-400' : 'text-neutral-500'}`}>{description}</p>
            </div>

            <div className={`w-full h-px mb-8 ${highlight ? 'bg-neutral-700' : 'bg-neutral-100'}`} />

            <ul className="space-y-4 mb-10 flex-1">
                {features.map((feature, i) => (
                    <li key={i} className={`flex items-start gap-3 text-sm font-semibold ${feature.locked ? 'opacity-40' : ''}`}>
                        <div className={`mt-0.5 p-1 rounded-full flex-shrink-0 ${feature.locked
                            ? highlight ? 'bg-neutral-700 text-neutral-500' : 'bg-neutral-100 text-neutral-300'
                            : highlight ? 'bg-orange-500 text-white' : 'bg-neutral-900 text-white'
                            }`}>
                            {feature.locked
                                ? <Lock className="w-3 h-3" strokeWidth={3} />
                                : <Check className="w-3 h-3" strokeWidth={3} />
                            }
                        </div>
                        <span className={feature.locked
                            ? highlight ? 'text-neutral-600' : 'text-neutral-300'
                            : highlight ? 'text-neutral-200' : 'text-neutral-700'
                        }>
                            {feature.text}
                        </span>
                    </li>
                ))}
            </ul>

            <Link href="/login?mode=signup" className="block mt-auto">
                <span className={`w-full inline-flex justify-center items-center py-4 rounded-xl font-bold transition-all duration-300 text-sm gap-2 ${highlight
                    ? 'bg-orange-500 text-white hover:bg-orange-400 shadow-lg shadow-orange-500/30'
                    : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100 border border-neutral-200 hover:border-neutral-300'
                    }`}>
                    {ctaText}
                    <ArrowRight size={16} />
                </span>
            </Link>
        </m.div>
    );
};

export const PricingTable = () => {
    const [billingCycle, setBillingCycle] = React.useState<'monthly' | 'yearly'>('monthly');

    const tiers = [
        {
            tier: 'Free',
            price: '$0',
            period: '/mo',
            description: 'Test drive your AI marketing agents before hiring.',
            badge: undefined,
            highlight: false,
            ctaText: 'Start For Free',
            features: [
                { text: 'Manual Copilot Mode (Approve everything)' },
                { text: '50 Intent-driven leads / month' },
                { text: 'Autonomous Sales Sequences (2/day)' },
                { text: 'Basic Social Media Drafting' },
                { text: 'Campaigns Hub (Limited view)' },
                { text: 'Full Autopilot Mode', locked: true },
                { text: 'AI Image Generation', locked: true },
                { text: 'Autonomous A/B Testing', locked: true },
            ] as Feature[]
        },
        {
            tier: 'Pro',
            price: '$5',
            period: '/mo',
            yearlyPrice: '$48',
            description: 'Hire 24/7 AI Sales & Marketing Agents.',
            badge: 'Start here',
            highlight: false,
            ctaText: 'Hire your Agents ($5/mo)',
            features: [
                { text: 'Fully Autonomous Autopilot Mode' },
                { text: '150 leads + High-Velocity Outreach (6/day)' },
                { text: 'Automated Daily Social Campaigns' },
                { text: 'AI Image Generation (Flux-1)' },
                { text: 'Autonomous A/B Testing (Basic)' },
                { text: 'Competitive Sentinel (Level 1)' },
                { text: 'Autonomous Role Discovery', locked: true },
                { text: 'AI Video Avatars (HeyGen)', locked: true },
            ] as Feature[]
        },
        {
            tier: 'Premium',
            price: '$99',
            period: '/mo',
            yearlyPrice: '$950',
            description: 'Deploy a full self-evolving AI revenue engine.',
            badge: 'Most Powerful',
            highlight: true,
            ctaText: 'Unlock Premium',
            features: [
                { text: 'Unlimited Lead Scrape & Outreach' },
                { text: 'Daily Master Strategy Execution (CMO Loop)' },
                { text: 'AI Video Avatars (HeyGen)' },
                { text: 'Autonomous Tool & Role Discovery' },
                { text: 'Advanced Psychographic Profiling' },
                { text: 'Triple-Threat Outreach' },
                { text: 'Competitive Sentinel (Advanced)' },
                { text: 'Advanced A/B Testing & Pivot Logic' },
                { text: 'Revenue Journey Tracking' },
                { text: 'White-glove 1:1 Onboarding' },
            ] as Feature[]
        }
    ];

    return (
        <section id="pricing" className="py-28 bg-white border-y border-neutral-200 relative">
            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <m.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-50 border border-neutral-200 text-neutral-700 text-xs font-bold mb-8 uppercase tracking-widest shadow-sm">
                            <Tag size={13} className="text-orange-500" /> Pricing
                        </span>
                        <h2 className="text-4xl sm:text-5xl lg:text-7xl font-black text-neutral-900 mb-6 tracking-tight leading-[1.05]">
                            Start Small. <br className="sm:hidden" />
                            <span className="text-neutral-400 font-light italic">Scale Big.</span>
                        </h2>
                        <p className="text-xl text-neutral-500 mb-10 font-medium leading-relaxed">
                            Try for free. Pay $5 to see real results. Unlock the Cognitive Brain when you&apos;re ready for full autonomy.
                        </p>

                        {/* Toggle */}
                        <div className="flex items-center justify-center gap-4">
                            <span className={`text-sm font-black uppercase tracking-wider transition-colors ${billingCycle === 'monthly' ? 'text-neutral-900' : 'text-neutral-400'}`}>Monthly</span>
                            <button
                                onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                                className={`relative w-16 h-8 rounded-full p-1 transition-colors duration-300 focus:outline-none cursor-pointer ${billingCycle === 'yearly' ? 'bg-neutral-900' : 'bg-neutral-200'}`}
                            >
                                <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center transform transition-transform duration-300 ${billingCycle === 'yearly' ? 'left-9' : 'left-1'}`}>
                                    {billingCycle === 'yearly' && <Crown size={10} className="text-orange-500" />}
                                </div>
                            </button>
                            <span className={`text-sm font-black uppercase tracking-wider transition-colors flex items-center gap-2 ${billingCycle === 'yearly' ? 'text-neutral-900' : 'text-neutral-400'}`}>
                                Yearly
                                <span className="text-emerald-600 text-[10px] font-bold bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">Save 20%</span>
                            </span>
                        </div>
                    </m.div>
                </div>

                <div className="grid lg:grid-cols-3 gap-6 max-w-[68rem] mx-auto items-stretch">
                    {tiers.map((tier, i) => (
                        <m.div
                            key={tier.tier}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1, duration: 0.5 }}
                        >
                            <PricingCard {...tier} billingCycle={billingCycle} />
                        </m.div>
                    ))}
                </div>

                {/* Cognitive Brain upsell note */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="mt-14 max-w-2xl mx-auto"
                >
                    <div className="flex flex-col sm:flex-row items-center gap-5 px-6 py-5 bg-neutral-50 border border-neutral-200 rounded-2xl text-center sm:text-left">
                        <div className="w-10 h-10 rounded-full bg-neutral-900 flex items-center justify-center flex-shrink-0">
                            <Brain size={18} className="text-white" />
                        </div>
                        <p className="text-sm text-neutral-600 font-medium leading-relaxed">
                            <span className="text-neutral-900 font-bold">The Brain is only on Cognitive.</span>{' '}
                            It&apos;s the intelligence layer that makes agents coordinate, analyze competitors, run A/B tests, and autonomously adapt your strategy.
                        </p>
                    </div>
                </m.div>
            </div>
        </section>
    );
};
